import { Section } from "@/components/ds/section";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { MessageCircle, Mail, MapPin } from "lucide-react";
import { canonicalLink, canonicalMeta, ogImageMeta } from "@/lib/seo";
import { publicSiteSettingsQuery } from "@/routes/_public/route";
import { supportWhatsAppUrl } from "@/lib/social";
import { CONTACT_EMAIL } from "@/lib/legal/seller";
import { WHATSAPP_PREFILL, formatPhoneGrouped, socialPills } from "@/data/about-contact";
import listening from "@/assets/dr-sara-listening-circle.jpg.asset.json";
import { ConsultBand } from "@/components/public/consult-band";
import { blogServicesQ } from "@/features/blog/queries";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";

import { btn, eyebrow } from "@/components/ds/styles";
const tile = "grid h-12 w-12 shrink-0 place-items-center rounded-full";

export const Route = createFileRoute("/_public/contact")({
  loader: ({ context }) => context.queryClient.ensureQueryData(blogServicesQ()),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  head: () => ({
    meta: [
      { title: "التواصل مع د. سارة النحّاس — واتساب، بريد، والعيادة بدمشق" },
      { name: "description", content: "واتساب العيادة +963 958 645 116، البريد hi@drsaranahas.com، والعيادة في أبو رمانة بدمشق بموعد مسبق. للاستشارات والدفع وأسئلة البوصلة." },
      { property: "og:title", content: "التواصل مع د. سارة النحّاس — واتساب، بريد، والعيادة بدمشق" },
      { property: "og:description", content: "واتساب العيادة +963 958 645 116، البريد hi@drsaranahas.com، والعيادة في أبو رمانة بدمشق بموعد مسبق. للاستشارات والدفع وأسئلة البوصلة." },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "ar_SY" },
      { property: "og:site_name", content: "د. سارة النحّاس" },
      ...ogImageMeta(),
      ...canonicalMeta("/contact"),
    ],
    links: [canonicalLink("/contact")],
  }),
  component: Contact,
});

function Contact() {
  const services = Route.useLoaderData();
  const { queryClient } = Route.useRouteContext();
  if (queryClient.getQueryData(blogServicesQ().queryKey) === undefined) {
    queryClient.setQueryData(blogServicesQ().queryKey, services);
  }
  const { data: settings } = useQuery(publicSiteSettingsQuery());
  const whatsappUrl = settings?.clinic_whatsapp ? supportWhatsAppUrl(settings.clinic_whatsapp, WHATSAPP_PREFILL) : "";
  const phone = formatPhoneGrouped(settings?.clinic_whatsapp);
  const address = (settings?.clinic_address_ar ?? "").trim();
  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT_EMAIL);
      toast.success("انتسخ البريد");
    } catch {
      toast.error("ما قدرنا ننسخ البريد");
    }
  };

  return (
    <div dir="rtl">
      <Section tone="cream" className="pb-14 lg:pb-20">
      <section className="mx-auto max-w-[76rem] px-5 pt-8 lg:pt-14">
        <p className={eyebrow}>التواصل</p>
        <h1 className="mt-2 font-display text-[42px] font-extrabold leading-tight text-text-primary lg:text-[58px]">
          <span className="mark">واتساب</span> أسرع طريق.
        </h1>
        <p className="mt-3 max-w-2xl text-[19px] leading-[1.8] text-text-secondary">
          هو قناة التواصل الفعلية: للاستشارات، للدفع، ولأي سؤال عن البوصلة.
        </p>
      </section>

      <div className="mx-3 mt-8 grid gap-4 lg:mx-auto lg:max-w-[76rem] lg:grid-cols-2 lg:items-start">
        <section className="rounded-ds-xl bg-green-100 p-6">
          <div className="flex items-center gap-3">
            <span className={tile + " bg-surface text-green-800"}>
              <MessageCircle className="h-6 w-6" strokeWidth={1.75} aria-hidden />
            </span>
            <div>
              <h2 className="font-display text-[23px] font-extrabold text-text-primary">واتساب</h2>
              {phone && <p dir="ltr" className="text-end font-display text-[15px] font-bold text-text-primary">{phone}</p>}
            </div>
          </div>
          {whatsappUrl && (
            <a
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={btn.primary + " mt-5 w-full"}
            >
              المراسلة على واتساب
            </a>
          )}
        </section>

        <section className="rounded-ds-xl bg-cream-50 px-6 py-6">
          <div className="flex items-center gap-3">
            <span className={tile + " bg-blue-100 text-blue-700"}>
              <Mail className="h-6 w-6" strokeWidth={1.75} aria-hidden />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="font-display text-[17px] font-bold text-text-primary">البريد</h2>
              <a href={`mailto:${CONTACT_EMAIL}`} dir="ltr" className="flex min-h-11 items-center justify-end text-[15px] text-text-link hover:underline">{CONTACT_EMAIL}</a>
            </div>
            <button
              type="button"
              onClick={() => void copyEmail()}
              className="inline-flex min-h-11 items-center rounded-ds-pill px-4 font-display text-[14px] font-bold text-text-secondary hover:bg-cream-100"
            >
              نسخ
            </button>
          </div>
          <div className="mt-5 border-t border-hairline-subtle pt-5">
            <div className="flex items-center gap-3">
              <span className={tile + " bg-amber-100 text-amber-800"}>
                <MapPin className="h-6 w-6" strokeWidth={1.75} aria-hidden />
              </span>
              <div>
                <h2 className="font-display text-[17px] font-bold text-text-primary">العيادة</h2>
                {address && <p className="text-[15px] text-text-secondary">{address}</p>}
              </div>
            </div>
            <p className="mt-4 text-[15px] leading-[1.8] text-text-secondary">
              الزيارة بموعد بس — اليوم والساعة بنتفق عليهم على واتساب قبل الطلب. بالعيادة في كمان دفع كاش.
            </p>
          </div>
        </section>
      </div>
      </Section>

      <Section tone="white" inner="mx-auto max-w-[76rem] px-5 py-14 lg:py-20">
        <p className={eyebrow}>متابعة د. سارة</p>
        <h2 className="mt-1 font-display text-[30px] font-extrabold text-text-primary">الشبكات للمحتوى</h2>
        <ul className="mt-4 flex flex-wrap gap-2">
          {socialPills(settings?.instagram_url).map((s) => (
            <li key={s.id}>
              <a
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                className="lift inline-flex min-h-11 items-center rounded-ds-pill border border-hairline-subtle bg-surface-alt px-5 font-display text-[15px] font-bold text-text-primary hover:bg-cream-100"
              >
                {s.label}
                {s.figure && <> · <bdi dir="ltr">{s.figure}</bdi></>}
              </a>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-[15px] text-text-secondary">للاستشارة والدفع — واتساب.</p>
      </Section>

      <Section tone="cream" className="py-14 lg:py-20">
        <div>
        <ConsultBand
          photo={{ src: listening.url, alt: "د. سارة النحّاس", width: 460, height: 460 }}
          title="السؤال أكبر من رسالة؟"
          text="استشارة 45 دقيقة مع د. سارة، أونلاين أو بالعيادة."
          footer="سؤال سريع؟ المراسلة على واتساب من فوق."
        />
        </div>
      </Section>
    </div>
  );
}
